/**
 * 路由守卫组件
 *
 * @experimental 此组件尚未在消费项目中广泛使用，API 可能调整。
 * 大多数 UmiJS 项目倾向于在 wrappers 中直接使用 useModel + Navigate。
 *
 * UmiJS 路由守卫通用模式：
 * - AuthGuard: 登录验证，未登录重定向到登录页
 * - GuestGuard: 游客页面，已登录重定向到首页
 *
 */

import type React from 'react';

/**
 * AuthGuard 组件属性
 */
export interface AuthGuardProps {
  /** 是否已登录 */
  isAuthenticated: boolean;
  /** 登录页路径，默认 '/login' */
  loginPath?: string;
  /**
   * 重定向渲染函数
   *
   * 返回一个 React 元素用于执行重定向（如 `<Navigate to={path} />`）。
   *
   * @param path - 目标路径
   * @returns 重定向 React 元素
   */
  redirect: (path: string) => React.ReactElement;
  /** 加载中组件（检查登录状态时展示） */
  loading?: React.ReactNode;
  /** 是否正在检查登录状态 */
  checking?: boolean;
  /** 子组件 */
  children: React.ReactNode;
}

/**
 * 登录验证守卫
 *
 * 用于保护需要登录才能访问的路由，未登录时重定向到登录页。
 *
 * @example
 * ```tsx
 * // UmiJS wrappers/auth.tsx
 * import { Navigate, Outlet, useModel } from '@umijs/max';
 *
 * export default function AuthWrapper() {
 *   const { initialState } = useModel('@@initialState');
 *   return (
 *     <AuthGuard
 *       isAuthenticated={!!initialState?.currentUser}
 *       redirect={(path) => <Navigate to={path} replace />}
 *       loading={<PageLoading />}
 *       checking={initialState === undefined}
 *     >
 *       <Outlet />
 *     </AuthGuard>
 *   );
 * }
 * ```
 */
export function AuthGuard({
  isAuthenticated,
  loginPath = '/login',
  redirect,
  loading,
  checking = false,
  children,
}: AuthGuardProps): React.ReactElement | null {
  if (checking) {
    return (loading as React.ReactElement) ?? null;
  }

  if (!isAuthenticated) {
    return redirect(loginPath);
  }

  return children as React.ReactElement;
}

/**
 * GuestGuard 组件属性
 */
export interface GuestGuardProps {
  /** 是否已登录 */
  isAuthenticated: boolean;
  /** 登录后跳转路径，默认 '/' */
  homePath?: string;
  /**
   * 重定向渲染函数
   *
   * 返回一个 React 元素用于执行重定向（如 `<Navigate to={path} />`）。
   *
   * @param path - 目标路径
   * @returns 重定向 React 元素
   */
  redirect: (path: string) => React.ReactElement;
  /** 子组件 */
  children: React.ReactNode;
}

/**
 * 游客页面守卫
 *
 * 用于登录/注册等页面，已登录用户访问时重定向到首页。
 *
 * @example
 * ```tsx
 * // UmiJS wrappers/guest.tsx
 * import { Navigate, Outlet, useModel } from '@umijs/max';
 *
 * export default function GuestWrapper() {
 *   const { initialState } = useModel('@@initialState');
 *   return (
 *     <GuestGuard
 *       isAuthenticated={!!initialState?.currentUser}
 *       redirect={(path) => <Navigate to={path} replace />}
 *     >
 *       <Outlet />
 *     </GuestGuard>
 *   );
 * }
 * ```
 */
export function GuestGuard({
  isAuthenticated,
  homePath = '/',
  redirect,
  children,
}: GuestGuardProps): React.ReactElement | null {
  if (isAuthenticated) {
    return redirect(homePath);
  }

  return children as React.ReactElement;
}
